import { ConfigFieldValue, DataInput } from '@/types/automationFields.ts';
import SelectField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/SelectField.tsx';
import TextAreaField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/TextAreaField.tsx';
import TextInputField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/TextInputField.tsx';
import DataInputSelectField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/DataInputSelectField.tsx';
import { Divider } from '@mantine/core';
import WebhookField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/WebhookField.tsx';
import HelperTextField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/HelperTextField.tsx';
import CheckboxField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/CheckboxInputField.tsx';
import DataInputMultiSelectField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/DataInputMultiSelectField.tsx';
import EmailField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/EmailField.tsx';
import BrowserMultiStageFlowField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/BrowserMultiStageFlowField.tsx';
import BrowserSecretBindingsField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/BrowserSecretBindingsField.tsx';
import JsonField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/JsonField.tsx';
import {
  ConfigFieldType,
  IntegrationCheckboxConfigField,
  IntegrationConfigField,
  IntegrationDataInputMultiSelectConfigField,
  IntegrationDataInputSelectConfigField,
  IntegrationHelperTextConfigField,
  IntegrationNumberConfigField,
  IntegrationSelectConfigField,
  IntegrationTextConfigField,
  IntegrationWebhookConfigField,
} from '@/types/integrations.ts';
import NumberInputField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldTypes/NumberInputField.tsx';

type props = {
  stepId: string;
  field: IntegrationConfigField;
  fieldValue: any;
  fieldValueConfig: ConfigFieldValue;
  onChangeDelay: (fieldId: string, updated_value: any) => void;
  onChangeNoDelay: (fieldId: string, updated_value: any) => void;
  disabled: boolean;
};

const IntegrationField = ({
  stepId,
  field,
  fieldValue,
  fieldValueConfig,
  onChangeDelay,
  onChangeNoDelay,
  disabled,
}: props) => {
  if (field.type === ConfigFieldType.Select) {
    return (
      <SelectField
        stepId={stepId}
        field={field as IntegrationSelectConfigField}
        fieldValue={fieldValue}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeNoDelay(field.id, updated_value)}
        disabled={disabled}
      />
    );
  } else if (field.type === ConfigFieldType.TextArea) {
    return (
      <TextAreaField
        stepId={stepId}
        field={field as IntegrationTextConfigField}
        fieldValue={fieldValue}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeDelay(field.id, updated_value)}
      />
    );
  } else if (field.type === ConfigFieldType.TextInput) {
    return (
      <TextInputField
        stepId={stepId}
        field={field as IntegrationTextConfigField}
        fieldValue={fieldValue}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeDelay(field.id, updated_value)}
      />
    );
  } else if (field.type === ConfigFieldType.NumberInput) {
    return (
      <NumberInputField
        stepId={stepId}
        field={field as IntegrationNumberConfigField}
        fieldValue={fieldValue}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeDelay(field.id, updated_value)}
      />
    );
  } else if (field.type === ConfigFieldType.Json) {
    if (field.id === 'browser_multi_stage_flow') {
      return (
        <BrowserMultiStageFlowField
          stepId={stepId}
          field={field as IntegrationTextConfigField}
          fieldValue={fieldValue}
          fieldValueConfig={fieldValueConfig}
          onChange={(updated_value) => onChangeDelay(field.id, updated_value)}
        />
      );
    }
    if (field.id === 'browser_secret_bindings') {
      return (
        <BrowserSecretBindingsField
          stepId={stepId}
          field={field as IntegrationTextConfigField}
          fieldValue={fieldValue}
          fieldValueConfig={fieldValueConfig}
          onChange={(updated_value) => onChangeDelay(field.id, updated_value)}
          disabled={disabled}
        />
      );
    }
    return (
      <JsonField
        stepId={stepId}
        field={field as IntegrationTextConfigField}
        fieldValue={fieldValue}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeDelay(field.id, updated_value)}
      />
    );
  } else if (field.type === ConfigFieldType.Checkbox) {
    return (
      <CheckboxField
        stepId={stepId}
        field={field as IntegrationCheckboxConfigField}
        fieldValue={fieldValue}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeNoDelay(field.id, updated_value)}
      />
    );
  } else if (field.type === ConfigFieldType.DataInputSelect) {
    return (
      <DataInputSelectField
        stepId={stepId}
        field={field as IntegrationDataInputSelectConfigField}
        fieldValue={fieldValue as DataInput | null | undefined}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeNoDelay(field.id, updated_value)}
      />
    );
  } else if (field.type === ConfigFieldType.DataInputMultiSelect) {
    return (
      <DataInputMultiSelectField
        stepId={stepId}
        field={field as IntegrationDataInputMultiSelectConfigField}
        fieldValue={fieldValue as DataInput[]}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeNoDelay(field.id, updated_value)}
      />
    );
  } else if (field.type === ConfigFieldType.Webhook) {
    return (
      <WebhookField
        stepId={stepId}
        field={field as IntegrationWebhookConfigField}
        fieldValue={fieldValue as DataInput}
        fieldValueConfig={fieldValueConfig}
      />
    );
  } else if (field.type === ConfigFieldType.HelperText) {
    return (
      <HelperTextField
        // stepId={stepId}
        field={field as IntegrationHelperTextConfigField}
        // fieldValue={fieldValue as DataInput}
        // fieldValueConfig={fieldValueConfig}
      />
    );
  } else if (field.type === ConfigFieldType.Divider) {
    return <Divider />;
  } else if (field.type === ConfigFieldType.Email) {
    return (
      <EmailField
        stepId={stepId}
        field={field as IntegrationTextConfigField}
        fieldValue={fieldValue}
        fieldValueConfig={fieldValueConfig}
        onChange={(updated_value) => onChangeDelay(field.id, updated_value)}
      />
    );
  }
  return <></>;
};

export default IntegrationField;
